import { Hsla, hslaToString } from 'dashboards/services/color';
import { InferenceDetection } from 'files/hooks/useImageInference';

import { Icon } from 'components/Icon/Icon';
import { Tooltip } from 'components/Tooltip/Tooltip';

type DetectionCropProps = {
  cropUrl: string | undefined;
  detection: InferenceDetection;
  color: Hsla;
  kind: 'person' | 'face';
  disabled?: boolean;
  isHovered: boolean;
  onHoverEnter: () => void;
  onHoverLeave: () => void;
  onSearch: () => void;
  onAddToWatchlist?: () => void;
};

export function DetectionCrop({
  cropUrl,
  detection,
  color,
  kind,
  disabled,
  isHovered,
  onHoverEnter,
  onHoverLeave,
  onSearch,
  onAddToWatchlist,
}: DetectionCropProps) {
  const label = kind === 'person' ? 'person' : 'face';
  const confidence = Math.round(detection.score * 100);

  const sizeClasses = kind === 'person' ? 'h-32 w-20' : 'h-20 w-20';
  const imgPositionClass = kind === 'person' ? 'object-top' : 'object-center';

  return (
    <div
      className="group relative shrink-0"
      onMouseEnter={disabled ? undefined : onHoverEnter}
      onMouseLeave={disabled ? undefined : onHoverLeave}
    >
      <button
        type="button"
        onClick={onSearch}
        disabled={disabled}
        className={`relative block overflow-hidden rounded-md border-2 transition-all ${sizeClasses} ${disabled ? 'cursor-wait' : ''}`}
        style={{
          borderColor: hslaToString(color, 1),
          backgroundColor: hslaToString(color, isHovered ? 0.3 : 0.12),
          boxShadow: isHovered
            ? `0 0 0 2px ${hslaToString(color, 0.35)}`
            : 'none',
        }}
        title={
          disabled
            ? 'Loading...'
            : `Search for similar ${label} (${confidence}% confidence)`
        }
      >
        {cropUrl ? (
          <img
            src={cropUrl}
            alt={`${label} detection`}
            className={`h-full w-full object-cover ${imgPositionClass}`}
          />
        ) : (
          <div className="h-full w-full animate-pulse bg-black/10" />
        )}

        <span
          className="absolute inset-0 flex items-center justify-center bg-black/45 transition-opacity"
          style={{ opacity: isHovered ? 1 : 0 }}
        >
          <Icon name="search" size="md" className="text-white" />
        </span>
      </button>

      {onAddToWatchlist && (
        <Tooltip content="Add to watchlist" side="top">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAddToWatchlist();
            }}
            disabled={disabled}
            className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-white bg-neutral-900 text-white shadow hover:bg-neutral-700 disabled:opacity-50"
            aria-label="Add to watchlist"
          >
            <Icon name="plus" size="xs" />
          </button>
        </Tooltip>
      )}
    </div>
  );
}
